// OTA: staging publish trigger (patch+1 versioning validation) — no-op.
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { scrubEvent } from './utils/sentryScrub';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import './index.css';
import App from './App';
import { hydratePartnerStatuses } from './constants/partnerStatuses';
import CrashScreen from './components/CrashScreen';

// ── Sentry ────────────────────────────────────────────────────────────
// Initialized BEFORE React renders so the SDK can hook React's error
// boundaries + capture init-time errors. No-ops when the DSN env var
// is unset (dev / preview deploys without a Sentry project).
// REACT_APP_SENTRY_DSN is the preferred path — CRA inlines it at
// ── 🚨 THE HARDCODED FALLBACK IS GONE. AN ABSENT DSN DISABLES SENTRY. ───────
//
// What was here: a committed DSN literal used whenever
// REACT_APP_SENTRY_DSN was falsy, on the reasoning that vercel.json's
// build.env "leaves the conditional falsy on production" and the DSN is a
// public client token so committing it is acceptable.
//
// 🚨 THE COST WAS NOT THE SECRECY OF THE DSN — it genuinely is public. The cost
// was that the integration became INVISIBLE TO CONFIGURATION. Sentry
// initialised whether or not anybody had configured it, so it could not be
// turned off for a preview deploy, a local build or an incident, and
// docs/data-flow-inventory.md — whose own header says it was built from a
// `process.env` grep — could never have found it. A recipient of borrower
// crash reports was not on the outbound inventory for four months because of
// this line.
//
// 🔑 THE DSN IS BUILD-TIME ENV AND NOTHING ELSE NOW. frontend/.env.production
// carries it, which is the file CRA inlines for `react-scripts build`, and
// vercel.json's build.env carries the same value. No DSN, no Sentry — which is
// the behaviour a preview deploy wanted all along.
const RESOLVED_SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (RESOLVED_SENTRY_DSN) {
  Sentry.init({
    dsn: RESOLVED_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay — masks all text and blocks media. Critical for a
      // mortgage app where every page can have SSN, DOB, employer,
      // bank-account, or credit-score data visible.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,    // 10% of sessions captured for replay
    replaysOnErrorSampleRate: 1.0,    // 100% of error sessions captured
    // 🚨 THE SCRUB MOVED TO utils/sentryScrub AND GREW THE KEYS IT WAS MISSING.
    // What was here matched ssn|itin|dob|date_of_birth|password against a
    // breadcrumb body WHEN IT WAS A STRING — so the §1002.6(b)(9) monitoring
    // keys (govt_ethnicity, govt_race, govt_sex, govt_info_provided),
    // marital_status and the three date-of-birth parts were never named, and
    // event.request, event.extra and event.contexts were never touched at all.
    // See that file for why it is a module rather than a closure.
    beforeSend: scrubEvent,
  });

  // Client-platform telemetry on the FRONTEND Sentry scope. WKWebView-vs-browser
  // divergence errors surface here (not in the backend Sentry), so this is where
  // the tag is most diagnostically useful. Same 'web'-default-on-error contract
  // as the request header and the backend middleware tag — they answer different
  // questions (frontend render errors vs. API errors) so we set both.
  try {
    Sentry.setTag('client_platform', Capacitor.getPlatform() || 'web');
  } catch (_) {
    Sentry.setTag('client_platform', 'web');
  }
}

// Pull partner status COPY from the backend (GET /api/knowledge) so the portal
// and Eva describe a buyer's status with the same sentences. Fire-and-forget:
// the module ships with bootstrap copy, so a failure leaves the UI correct
// rather than blank.
hydratePartnerStatuses();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {/* ⚠️ THE FALLBACK IS A RENDER PROP NOW, so the boundary can pass the
        error, the component stack and the Sentry event id to the screen
        instead of discarding them. See components/CrashScreen.jsx — a crash
        that cannot say what it was costs a round trip with the user every
        time it happens. */}
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, eventId }) => (
        <CrashScreen error={error} componentStack={componentStack} eventId={eventId} />
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// ── Capgo OTA — confirm the bundle actually booted ────────────────────
// notifyAppReady() tells the native updater THIS over-the-air bundle rendered
// successfully. If it never fires (white-screen / boot crash before anything
// paints), Capgo's native auto-rollback reverts to the last-known-good bundle
// on the next launch. So we must NOT call it eagerly — we wait until React has
// genuinely committed content into #root, so "ready" reflects a real render,
// not merely that the JS file loaded. No-op off native (browser / PWA).
//
// Tradeoff: if a render error is caught by the Sentry ErrorBoundary above, the
// graceful fallback UI paints into #root and the bundle is treated as "booted"
// (Sentry alerts on that error separately). Only a crash severe enough to paint
// NOTHING triggers the OTA auto-rollback. That is the intended white-screen
// guard, not a general "any error rolls back" mechanism.
if (Capacitor.isNativePlatform()) {
  const rootEl = document.getElementById('root');
  const confirmBooted = () => {
    if (rootEl && rootEl.childElementCount > 0) {
      CapacitorUpdater.notifyAppReady().catch((err) => {
        console.error('[OTA] notifyAppReady failed:', err);
      });
    } else {
      // Not painted yet — check again next frame (bounded by the browser's
      // frame cadence; a bundle that never paints simply never confirms).
      requestAnimationFrame(confirmBooted);
    }
  };
  requestAnimationFrame(confirmBooted);
}

// ── PWA Service Worker registration ───────────────────────────────────
// Scaffolding only — no caching yet (Phase 2). The empty SW exists so
// Chrome/Edge will surface the desktop install prompt; iOS/Android
// installability already works on manifest alone. Phase 3 will add a
// network-first fetch handler for API calls + cache-first for static
// assets, with a versioned cache + skipWaiting/clients.claim already
// wired up here so existing installs receive new code on next load.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('[SW] Registered:', registration.scope);
      })
      .catch((error) => {
        console.error('[SW] Registration failed:', error);
      });
  });
}
// cache bust Wed Jun 17 13:30:32 CDT 2026
// OTA staging trigger — touch frontend/src to fire publish-staging (2026-07-07)
